'use client';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import NextImage from 'next/image';
import { Icon } from '@ui/icons';
import { Tooltip, PillText } from '@web/atoms';
import { Modal } from '@web/components/Modal';
import { EmailCapture } from '@web/components/EmailCaptureBar/EmailCapture';
import checkIcon from '../../images/Icons/check-in-circle-lilac.svg';
import bellIcon from '../../images/Icons/bell.svg';
import successBellIcon from '../../images/Icons/bell-success.svg';
import {
  Card,
  CardWrapper,
  Info,
  StyledImage,
  Name,
  Description,
  CardContent,
  CardHeader,
  MoreDetails,
  BadgeWrapper,
  IconContainer,
  GetNotified,
  GetNotifiedModalContent,
  GetNotifiedModalImage,
  GetNotifiedModalHeading,
  GetNotifiedModalDescription,
  GetNotifiedModalCapture,
  SuccessPopUpBar,
} from './styles.js';

export const RecommendedProductCard = ({
  label,
  title,
  badge,
  badgeVariant = 'dark',
  description,
  checked = false,
  img,
  imgSize,
  onChange,
  onDetailsClick,
  showMoreDetails,
  unavailable,
  unavailableText = 'Unavailable due to allergy concern',
  notSelectable,
  notSelectableText = 'To choose this item, please remove one of your previous selections.',
  level,
  orientation = 'row',
  mobileOrientation,
  offsetBadge = false,
  width = 345,
  mobileWidth,
  bgColor = 'lightest',
  typography = 'heading4',
  showCheckmark = false,
  alignColumnContent = 'left',
  borderRadius = 24,
  outOfStock = false,
  getNotified = false,
  getNotifiedText = 'Get Notified',
  modal,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [showSuccessPopUp, setShowSuccessPopUp] = useState(false);
  const [isNotified, setIsNotified] = useState(false);

  const openGetNotifiedModal = e => {
    e.stopPropagation();
    if (modal) setShowModal(true);
  };

  const closeGetNotifiedModal = () => setShowModal(false);

  const onGetNotifiedSuccess = () => {
    closeGetNotifiedModal();
    setIsNotified(true);
    setShowSuccessPopUp(true);
    setTimeout(() => setShowSuccessPopUp(false), 5000);
  };

  const getProductCard = props => (
    <CardWrapper $offsetBadge={offsetBadge ? 10 : 0}>
      {outOfStock ? (
        <BadgeWrapper $outOfStock={outOfStock}>
          {' '}
          <PillText
            text={'Out of stock'}
            variant="disabled"
            size="large"
            capitalizeText={false}
          ></PillText>
        </BadgeWrapper>
      ) : (
        offsetBadge &&
        badge && (
          <BadgeWrapper>
            <PillText text={badge} variant={badgeVariant} size="large" />
          </BadgeWrapper>
        )
      )}
      <Card
        $orientation={orientation}
        $mobileOrientation={mobileOrientation ?? orientation}
        {...props}
        checked={checked}
        width={width}
        $mobileWidth={mobileWidth}
        $unavailable={unavailable || outOfStock}
        $notSelectable={notSelectable}
        $outOfStock={outOfStock}
        $bgColor={bgColor}
        aria-label={`${label} ${title}`}
        onClick={!notSelectable && !outOfStock ? onChange : undefined}
        $offsetBadge={offsetBadge}
        $borderRadius={borderRadius}
      >
        <CardContent
          $orientation={orientation}
          $mobileOrientation={mobileOrientation ?? orientation}
          $offsetBadge={offsetBadge && badge}
          $alignColumnContent={alignColumnContent}
        >
          <StyledImage
            checked={checked}
            $unavailable={unavailable || outOfStock}
            $orientation={orientation}
            $mobileOrientation={mobileOrientation ?? orientation}
            $imgWidth={imgSize}
            $imgHeight={imgSize}
            $alignColumnContent={alignColumnContent}
          >
            {img && (
              <NextImage src={img.src} alt={img.alt} width={72} height={72} />
            )}
          </StyledImage>
          {showCheckmark && (
            <IconContainer
              $orientation={orientation}
              $mobileOrientation={mobileOrientation ?? orientation}
              $offsetBadge={offsetBadge}
            >
              {checked && (
                <NextImage
                  src={checkIcon}
                  width={28}
                  height={28}
                  alt={'Check icon'}
                />
              )}
            </IconContainer>
          )}
          <Info
            checked={checked}
            $orientation={orientation}
            $mobileOrientation={mobileOrientation ?? orientation}
          >
            <CardHeader
              $orientation={orientation}
              $mobileOrientation={mobileOrientation ?? orientation}
              $alignColumnContent={alignColumnContent}
            >
              <Name as={level} $typography={typography}>
                {title}
              </Name>
            </CardHeader>
            <Description>
              {unavailable ? unavailableText : description}
            </Description>
            {outOfStock && getNotified ? (
              isNotified ? (
                <GetNotified
                  as="span"
                  $isNotified
                  $orientation={orientation}
                  $mobileOrientation={mobileOrientation ?? orientation}
                >
                  <NextImage
                    src={successBellIcon}
                    width={17}
                    height={19}
                    alt={'Bell icon'}
                  />
                  You’ll be notified
                </GetNotified>
              ) : (
                <GetNotified
                  type="button"
                  $unavailable={unavailable}
                  onClick={openGetNotifiedModal}
                  $orientation={orientation}
                  $mobileOrientation={mobileOrientation ?? orientation}
                >
                  <NextImage
                    src={bellIcon}
                    width={17}
                    height={19}
                    alt={'Bell icon'}
                  />
                  {getNotifiedText}
                </GetNotified>
              )
            ) : (
              showMoreDetails &&
              !unavailable &&
              !outOfStock && (
                <MoreDetails
                  type="button"
                  $unavailable={unavailable}
                  onClick={e => {
                    e.stopPropagation();
                    onDetailsClick();
                  }}
                  $orientation={orientation}
                  $mobileOrientation={mobileOrientation ?? orientation}
                >
                  View ingredients
                </MoreDetails>
              )
            )}
          </Info>
        </CardContent>
      </Card>
    </CardWrapper>
  );

  return (
    <>
      {notSelectable && !unavailable && !outOfStock ? (
        <Tooltip trigger={getProductCard} placement="top" offsetY={-200}>
          {notSelectableText}
        </Tooltip>
      ) : (
        getProductCard()
      )}
      {showSuccessPopUp && (
        <SuccessPopUpBar role="status">
          <Icon
            iconName="checkmark"
            alt="checkmark"
            color="text.lightest"
            size="15"
            id="notified_check"
          />
          You’ll be notified when {title} is back
        </SuccessPopUpBar>
      )}
      {modal && (
        <Modal
          shown={showModal}
          onCloseModal={closeGetNotifiedModal}
          ariaLabel={'get notified modal'}
          backgroundColor={modal.backgroundColor}
          mobileHalfScreen
          usePlumXCloseButton
        >
          <GetNotifiedModalContent>
            {modal.image && (
              <GetNotifiedModalImage>
                <NextImage
                  src={modal.image.src}
                  alt={modal.image.alt}
                  width={160}
                  height={140}
                />
              </GetNotifiedModalImage>
            )}
            {modal.heading && (
              <GetNotifiedModalHeading>{modal.heading}</GetNotifiedModalHeading>
            )}
            {modal.description && (
              <GetNotifiedModalDescription>
                {modal.description}
              </GetNotifiedModalDescription>
            )}
            {modal.emailCapture && (
              <GetNotifiedModalCapture>
                <EmailCapture
                  {...modal.emailCapture}
                  buttonType={modal.emailCapture.buttonType || 'cta'}
                  onSuccess={onGetNotifiedSuccess}
                  showSuccessToast={false}
                />
              </GetNotifiedModalCapture>
            )}
          </GetNotifiedModalContent>
        </Modal>
      )}
    </>
  );
};

RecommendedProductCard.displayName = 'RecommendedProductCard';
RecommendedProductCard.propTypes = {
  label: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  unavailable: PropTypes.bool,
  unavailableText: PropTypes.string,
  notSelectable: PropTypes.bool,
  notSelectableText: PropTypes.string,
  outOfStock: PropTypes.bool,
  getNotified: PropTypes.bool,
  getNotifiedText: PropTypes.string,
  modal: PropTypes.shape({
    image: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        src: PropTypes.string,
        alt: PropTypes.string,
        width: PropTypes.number,
        height: PropTypes.number,
      }),
    ]),
    heading: PropTypes.string,
    description: PropTypes.string,
    emailCapture: PropTypes.object,
    backgroundColor: PropTypes.string,
  }),
  description: PropTypes.node.isRequired,
  checked: PropTypes.bool,
  img: PropTypes.shape({
    src: PropTypes.string.isRequired,
    alt: PropTypes.string.isRequired,
  }).isRequired,
  imgSize: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  badge: PropTypes.string,
  badgeVariant: PropTypes.string,
  level: PropTypes.oneOf(['h1', 'h2', 'h3', 'h4', 'h5', 'h6']),
  orientation: PropTypes.oneOf(['row', 'column']),
  mobileOrientation: PropTypes.oneOf(['row', 'column']),
  offsetBadge: PropTypes.bool,
  width: PropTypes.number,
  mobileWidth: PropTypes.number,
  bgColor: PropTypes.oneOf(['base', 'lightest']),
  typography: PropTypes.string,
  showCheckmark: PropTypes.bool,
  alignColumnContent: PropTypes.string,
  borderRadius: PropTypes.number,
};
